import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  FacialBiometryErrorCode,
  FacialBiometryStatus
} from '@/constants/facial-biometry.constants'
import { getApiErrorMessage, hasApiErrorCode } from '@/utils/apiError'
import type {
  FacialBiometryDoctorItem,
  FacialBiometryListParams,
  FacialBiometryPaginationMeta,
  ValidateFacialBiometryPayload
} from '@/types/facial-biometry.types'

const createDefaultMeta = (): FacialBiometryPaginationMeta => ({
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0
})

const createDefaultFilters = (): FacialBiometryListParams => ({
  page: 1,
  limit: 20,
  sortOrder: 'ASC'
})

export const useFacialBiometryStore = defineStore('facialBiometry', () => {
  const toast = useToast()
  const { notifyError } = useErrorToast()

  const doctors = ref<FacialBiometryDoctorItem[]>([])
  const meta = ref<FacialBiometryPaginationMeta>(createDefaultMeta())
  const filters = ref<FacialBiometryListParams>(createDefaultFilters())
  const isLoading = ref(false)
  const isProcessing = ref(false)

  const validationQueue = ref<FacialBiometryDoctorItem[]>([])
  const currentStepIndex = ref(0)

  const currentDoctor = computed(() => validationQueue.value[currentStepIndex.value] ?? null)
  const isLastStep = computed(() => currentStepIndex.value === validationQueue.value.length - 1)
  const allValidated = computed(
    () => currentStepIndex.value >= validationQueue.value.length && validationQueue.value.length > 0
  )

  async function fetchDoctors(page?: number) {
    try {
      isLoading.value = true
      const params: FacialBiometryListParams = {
        ...filters.value,
        page: page ?? filters.value.page
      }
      const response = await Network.facialBiometry.listDoctors(params)
      doctors.value = response.data
      meta.value = response.meta
    } catch (error) {
      notifyError(error, 'Erro ao buscar biometrias faciais')
      console.error(error)
      doctors.value = []
    } finally {
      isLoading.value = false
    }
  }

  async function validateBiometry(
    id: number,
    payload: ValidateFacialBiometryPayload
  ): Promise<boolean> {
    try {
      isProcessing.value = true
      await Network.facialBiometry.validate(id, payload)
      const action = payload.status === 'VERIFIED' ? 'aprovada' : 'reprovada'
      toast.success(`Biometria facial ${action} com sucesso`)
      return true
    } catch (error) {
      notifyError(error, 'Erro ao validar a biometria facial')
      console.error(error)
      return false
    } finally {
      isProcessing.value = false
    }
  }

  async function notifyDoctor(doctorId: number): Promise<boolean> {
    try {
      isProcessing.value = true
      await Network.facialBiometry.notifyDoctor(doctorId)
      toast.success('Notificação enviada com sucesso')
      return true
    } catch (error: unknown) {
      // Os dois códigos são avisos (a ação não precisa ser refeita). Guard por
      // `errorCode`, que funciona com o axios cru e com o `ServiceError`.
      if (hasApiErrorCode(error, FacialBiometryErrorCode.NOTIFICATION_ALREADY_SENT_RECENTLY)) {
        // O backend interpola os minutos restantes na mensagem.
        toast.warning(getApiErrorMessage(error, 'Notificação já enviada recentemente.'))
      } else if (hasApiErrorCode(error, FacialBiometryErrorCode.DOCTOR_ALREADY_HAS_BIOMETRY)) {
        toast.warning('Este médico já possui biometria cadastrada.')
      } else {
        notifyError(error, 'Erro ao notificar o médico')
      }
      return false
    } finally {
      isProcessing.value = false
    }
  }

  function initValidationQueue(items: FacialBiometryDoctorItem[]) {
    validationQueue.value = items.filter((i) => i.status === FacialBiometryStatus.PENDING)
    currentStepIndex.value = 0
  }

  function nextStep() {
    if (currentStepIndex.value < validationQueue.value.length) {
      currentStepIndex.value++
    }
  }

  function setStepIndex(index: number) {
    currentStepIndex.value = index
  }

  function setFilters(newFilters: Partial<FacialBiometryListParams>) {
    filters.value = { ...filters.value, ...newFilters, page: 1 }
  }

  function resetFilters() {
    filters.value = createDefaultFilters()
  }

  function setPage(page: number) {
    filters.value.page = page
  }

  function $reset() {
    doctors.value = []
    meta.value = createDefaultMeta()
    filters.value = createDefaultFilters()
    isLoading.value = false
    isProcessing.value = false
    validationQueue.value = []
    currentStepIndex.value = 0
  }

  return {
    doctors,
    meta,
    filters,
    isLoading,
    isProcessing,
    validationQueue,
    currentStepIndex,
    currentDoctor,
    isLastStep,
    allValidated,
    fetchDoctors,
    validateBiometry,
    notifyDoctor,
    initValidationQueue,
    nextStep,
    setStepIndex,
    setFilters,
    resetFilters,
    setPage,
    $reset
  }
})
